/**
 * Folds a string to its searchable form: decomposes accents and drops the combining marks, lowercases
 * per locale, and collapses whitespace. Applied to both the indexed documents and the query, so
 * "Kärcher" matches "karcher".
 */
export const normalizeForSearch = (text: string, locale: string): string => {
    if (!text) {
        return "";
    }

    return text.normalize("NFKD").replace(/\p{M}/gu, "").toLocaleLowerCase(locale).replace(/\s+/g, " ").trim();
};

type NormalizationIndexMap = {
    readonly normalized: string;
    /** UTF-16 start offset in the original string for each character in `normalized`. */
    readonly normIndexToOrigStart: readonly number[];
};

const MARK_CHARACTER = /\p{M}/u;
const WHITESPACE_CHARACTER = /\s/;

/**
 * Builds the same normalised label Fuse searches, while recording where each normalised character
 * begins in the display string so match ranges can be painted back onto the original label.
 */
export const buildNormalizationIndexMap = (text: string, locale: string): NormalizationIndexMap => {
    if (!text) {
        return { normalized: "", normIndexToOrigStart: [] };
    }

    const withoutMarksChars: string[] = [];
    const withoutMarksOrigStart: number[] = [];

    let origOffset = 0;

    for (const char of text) {
        const origStart = origOffset;
        const decomposed = char.normalize("NFKD");

        for (let index = 0; index < decomposed.length; index++) {
            const decomposedChar = decomposed[index];

            if (!MARK_CHARACTER.test(decomposedChar)) {
                withoutMarksChars.push(decomposedChar);
                withoutMarksOrigStart.push(origStart);
            }
        }

        origOffset += char.length;
    }

    const lower = withoutMarksChars.join("").toLocaleLowerCase(locale);
    const lowerOrigStart = withoutMarksOrigStart.slice(0, lower.length);

    const collapsedChars: string[] = [];
    const collapsedOrigStart: number[] = [];

    for (let index = 0; index < lower.length; index++) {
        const char = lower[index];

        if (WHITESPACE_CHARACTER.test(char)) {
            if (collapsedChars.length > 0 && collapsedChars[collapsedChars.length - 1] !== " ") {
                collapsedChars.push(" ");
                collapsedOrigStart.push(lowerOrigStart[index]);
            }

            continue;
        }

        collapsedChars.push(char);
        collapsedOrigStart.push(lowerOrigStart[index]);
    }

    let start = 0;
    let end = collapsedChars.length;

    while (start < end && collapsedChars[start] === " ") {
        start++;
    }

    while (end > start && collapsedChars[end - 1] === " ") {
        end--;
    }

    return {
        normalized: collapsedChars.slice(start, end).join(""),
        normIndexToOrigStart: collapsedOrigStart.slice(start, end),
    };
};
